<template>
  <div v-if="started" class="screen-saver f-w f-h" @click="stop">
    <div class="shadow">
      <div class="info-title">{{ currentStep.name }}</div>
      <div class="info-description mt10">{{ currentStep.info }}</div>
    </div>
  </div>
</template>

<script>
/**
 * Handles the screen saver
 */

// Vuex
import { mapState } from 'vuex';
import { FETCH_SCREEN_SAVER } from '@/store/action-types';
import { GET_SCREEN_SAVER } from '@/store/mutation-types';
// Mixin
import mixinMeetingPages from '@/mixins/meeting-pages.vue';

export default {

  name: 'ScreenSaver',

  mixins: [mixinMeetingPages],

  data() {
    return {
      started: false,
      inactiveTimeout: null,
      nextStepTimeout: null,
      currentStepIndex: -1,
      routeBeforeStart: '',
    };
  },

  computed: {
    currentStep() {
      if (this.currentStepIndex >= 0 && this.currentStepIndex < this.pages.length) {
        return this.pages[this.currentStepIndex];
      }
      return {};
    },
    idleDuration() {
      const { screenSaverTimeout } = this.App.user;
      if (typeof screenSaverTimeout === 'undefined') {
        // Should buy enough time to wait for App.user to load
        return NaN;
      }
      return screenSaverTimeout * 1000;
    },
    screenSaverOn() {
      return this.App.user.screenSaverOn;
    },
    ...mapState({
      pages: state => state[GET_SCREEN_SAVER.stateKey] || [],
      pagesLoading: state => state[GET_SCREEN_SAVER.loadingKey],
    }),
  },

  watch: {
    idleDuration() {
      // idleDuration will change if user just loaded or screenSaverTimeout changes
      this.resetTimeout();
    },
    screenSaverOn() {
      this.resetTimeout();
    },
  },

  created() {
    this.resetTimeout();
    // Moving the mouse or touching the screen should prevent screen saver
    document.addEventListener('mousemove', this.resetTimeout);
    document.addEventListener('mousedown', this.resetTimeout);
    document.addEventListener('touchmove', this.resetTimeout);
    document.addEventListener('touchstart', this.resetTimeout);
    document.addEventListener('touchend', this.resetTimeout);
    document.addEventListener('keypress', this.resetTimeout);
  },

  beforeDestroy() {
    document.removeEventListener('mousemove', this.resetTimeout);
    document.removeEventListener('mousedown', this.resetTimeout);
    document.removeEventListener('touchmove', this.resetTimeout);
    document.removeEventListener('touchstart', this.resetTimeout);
    document.removeEventListener('touchend', this.resetTimeout);
    document.removeEventListener('keypress', this.resetTimeout);
    clearTimeout(this.nextStepTimeout);
    clearTimeout(this.inactiveTimeout);
  },

  methods: {
    /** Cancel the timeout to start the screen saver and reset it */
    resetTimeout() {
      if (isNaN(this.idleDuration)) {
        return;
      }
      clearTimeout(this.inactiveTimeout);
      if (this.screenSaverOn) {
        this.inactiveTimeout = setTimeout(() => {
          if (!this.$store.state.meeting.isMeetingInProgress) {
            this.start();
          }
        }, this.idleDuration);
      }
    },
    /** Start screen saver => automatically switch pages */
    start() {
      if (!this.currentTeam) return;
      if (!this.currentTeam.screenSaverKeepPage) {
        this.routeBeforeStart = this.$route.fullPath;
      }
      this.$store.dispatch(FETCH_SCREEN_SAVER).then(() => {
        this.started = true;
        this.nextStep();
      });
    },
    /** Stop screen saver */
    stop() {
      this.started = false;
      this.currentStepIndex = -1;
      clearTimeout(this.nextStepTimeout);
      this.resetTimeout();
      if (!this.App.user.screenSaverKeepPage) {
        this.$router.push(this.routeBeforeStart);
      }
    },
    /** Automatically push the next step's page */
    nextStep() {
      clearTimeout(this.nextStepTimeout);
      if (this.started) {
        this.currentStepIndex++;
        this.currentStepIndex %= this.pages.length;
        const link = this.getPageLink(this.currentStep);
        this.$router.push(link);
        this.nextStepTimeout = setTimeout(
          this.nextStep,
          this.currentStep.duration > 30 ? this.currentStep.duration * 1000 : 30000,
        );
      }
    },
  },
};
</script>

<style lang="scss">
.screen-saver {
  position: fixed;
  top: 0px;
  left: 0px;
  z-index: 11000;

  .shadow {
    box-shadow: 0px 0px 80px 90px rgba(2, 0, 39, 0.6);
    position: absolute;
    left: 0px;
    bottom: 0px;
    padding-bottom: 10px;
    width: 100%;
    background: rgba(2, 0, 39, 0.6);
    color: #ffffff;

    .info-title {
      font-size: 1.7em;
      text-align: center;
    }
    .info-description {
      text-align: center;
    }
  }
}
</style>
